'use client';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import { IRepositoriesContext } from '@/hooks/useRepositories.ts';
import { FreimaurerEntry } from '@/model/freimaurer.js';
import { ImpfstudienEntry } from '@/model/impfstudien.js';
import { InobhutnahmeEntry } from '@/model/inobhutnahme.js';
import { JustizversagenEntry } from '@/model/justizversagen';
import { MedienverleumdungEntry } from '@/model/medienverleumdung';
import { MobilfunkEntry } from '@/model/mobilfunk.js';
import { MobilfunkStudienEntry } from '@/model/mobilfunk-studien.js';
import { PharmaMedizinopferEntry } from '@/model/pharma-medizinopfer';
import { WahlbetrugEntry } from '@/model/wahlbetrug';
import { ZensierteErfindungEntry } from '@/model/zensierte-erfindung';
import { defaultMetaData, type MetaTypeProps } from '@/utils/metaDataDefaults.ts';

export type OverviewComponentProps = keyof IRepositoriesContext;

type MetaDataProps =
  | {
      component: OverviewComponentProps;
      data?: undefined;
    }
  | {
      component: 'freimaurerRepositoryDetail';
      data: FreimaurerEntry;
    }
  | {
      component: 'impfstudienRepositoryDetail';
      data: ImpfstudienEntry;
    }
  | {
      component: 'inobhutnahmeRepositoryDetail';
      data: InobhutnahmeEntry;
    }
  | {
      component: 'mobilfunkRepositoryDetail';
      data: MobilfunkEntry;
    }
  | {
      component: 'pharmaMedizinopferRepositoryDetail';
      data: PharmaMedizinopferEntry;
    }
  | {
      component: 'mobilfunkStudienRepositoryDetail';
      data: MobilfunkStudienEntry;
    }
  | {
      component: 'medienverleumdungRepositoryDetail';
      data: MedienverleumdungEntry;
    }
  | {
      component: 'wahlbetrugRepositoryDetail';
      data: WahlbetrugEntry;
    }
  | {
      component: 'justizversagenRepositoryDetail';
      data: JustizversagenEntry;
    }
  | {
      component: 'zensierteErfindungRepositoryDetail';
      data: ZensierteErfindungEntry;
    };

export { defaultMetaData };
export type { MetaTypeProps };

export default function MetaData({ component, data }: MetaDataProps) {
  const { t } = useTranslation();

  let title = '';
  let description = '';
  let keywords = '';
  let type: MetaTypeProps | undefined = undefined;

  switch (component) {
    case 'freimaurerRepository': {
      title = t('freimaurer.banner_title');
      description = t('freimaurer.banner_pr_text');
      keywords = t('freimaurer.keywords');
      break;
    }
    case 'impfstudienRepository': {
      title = t('common.impfschaeden');
      description = t('impfstudien.banner_pr_text');
      keywords = t('impfstudien.keywords');
      break;
    }
    case 'inobhutnahmeRepository': {
      title = t('common.kinder_inobhutnahme');
      description = t('inobhutnahme.banner_pr_text');
      keywords = t('inobhutnahme.keywords');
      break;
    }
    case 'mobilfunkRepository': {
      title = t('mobilfunk.banner_title');
      description = t('mobilfunk.banner_pr_text');
      keywords = t('mobilfunk.keywords');
      break;
    }
    case 'pharmaMedizinopferRepository': {
      title = t('pharma-medizinopfer.banner_title');
      description = t('pharma-medizinopfer.banner_pr_text');
      keywords = t('pharma-medizinopfer.keywords');
      break;
    }
    case 'mobilfunkStudienRepository': {
      title = t('mobilfunk.banner_title');
      description = t('mobilfunk.banner_pr_text');
      keywords = t('mobilfunk.keywords');
      break;
    }
    case 'medienverleumdungRepository': {
      title = t('startpage.medien_verleumdungen.title');
      description = t('startpage.medien_verleumdungen.pr_text');
      keywords = t('navigation.medienverleumdungen');
      break;
    }
    case 'wahlbetrugRepository': {
      title = t('startpage.wahlbetrug.title');
      description = t('startpage.wahlbetrug.pr_text');
      keywords = t('wahlbetrug.keywords');
      break;
    }
    case 'justizversagenRepository': {
      title = t('startpage.justizversagen.title');
      description = t('startpage.justizversagen.pr_text');
      keywords = t('justizversagen.keywords');
      break;
    }
    case 'zensierteErfindungRepository': {
      title = t('startpage.zensierte-erfindung.title');
      description = t('startpage.zensierte-erfindung.pr_text');
      keywords = t('zensierte-erfindung.keywords');
      break;
    }
    case 'freimaurerRepositoryDetail': {
      title = `${t('freimaurer.banner_title')} - ${data.name} - ${data.adress}, ${data.country}`;
      description = data.essence_of_research || '';
      keywords = (data.category || '') + (data.typ || '') + (data.typ_others || '');
      type = 'article';
      break;
    }
    case 'impfstudienRepositoryDetail': {
      title = data.title;
      description = data.abstract || '';
      keywords =
        (data.author || '') + (data.vaccine || '') + (data.health_damage || '') + (data.vaccine_manufacturer || '');
      type = 'article';
      break;
    }
    case 'inobhutnahmeRepositoryDetail': {
      title = `${t('common.kinder_inobhutnahme')} - ${data.Name} - ${data.city}, ${data.country}`;
      description = data.text || '';
      keywords =
        (data.executing_authority || '') + (data.other_parties_involved || '') + (data.family_background || '');
      type = 'article';
      break;
    }
    case 'pharmaMedizinopferRepositoryDetail': {
      title = `${t('pharma-medizinopfer.banner_title')} - ${data.firstname_damaged} - ${data.city}, ${data.country}`;
      description = data.remark || '';
      keywords = (data.symptom_disease || '') + (data.symptom_addition || '');
      type = 'article';
      break;
    }
    case 'mobilfunkRepositoryDetail': {
      title = `${t('mobilfunk.banner_title')} - ${data.firstname_damaged} - ${data.city}, ${data.country}`;
      description = data.remark || '';
      keywords =
        (data.symptom_disease || '') +
        (data.symptom_addition || '') +
        (data.radiation_source || '') +
        (data.radiation_source_spez || '');
      type = 'article';
      break;
    }
    case 'mobilfunkStudienRepositoryDetail': {
      title = data.title;
      description = data.abstract || '';
      keywords = (data.author || '') + (data.health_damage || '') + (data.journal_name || '');
      type = 'article';
      break;
    }
    case 'medienverleumdungRepositoryDetail': {
      title = `${t('startpage.medien_verleumdungen.title')} - ${data.title || data.person || ''}`;
      description = data.what_happened || data.my_correction || '';
      keywords = (data.person || '') + (data.media || '') + (data.title || '');
      type = 'article';
      break;
    }
    case 'wahlbetrugRepositoryDetail': {
      title = `${t('startpage.wahlbetrug.title')} - ${data.Title || ''}`;
      description = data.what_happened || '';
      keywords = t('wahlbetrug.keywords');
      type = 'article';
      break;
    }
    case 'justizversagenRepositoryDetail': {
      title = `${t('startpage.justizversagen.title')} - ${data.title || ''}`;
      description = data.what_happened || data.subject || '';
      keywords = t('justizversagen.keywords');
      type = 'article';
      break;
    }
    case 'zensierteErfindungRepositoryDetail': {
      title = `${t('startpage.zensierte-erfindung.title')} - ${data.title || ''}`;
      description = data.censorship || data.invention || '';
      keywords = t('zensierte-erfindung.keywords');
      type = 'article';
      break;
    }
  }

  useEffect(() => {
    setMetaData({
      title: title.length > 100 ? `${title.slice(0, 100)}...` : title,
      description: description.length > 275 ? `${description.slice(0, 275)}...` : description,
      keywords: keywords.split(','),
      type
    });
    return () => {
      setDefaultMetaData();
    };
  }, [title, description, keywords, type]);

  return null;
}

export function setMetaData({
  title,
  description,
  keywords,
  url,
  image,
  author,
  type,
  locale
}: {
  title?: string;
  description?: string;
  keywords?: string[];
  url?: string;
  image?: string;
  author?: string;
  type?: MetaTypeProps;
  locale?: string;
}) {
  // Browser-Title setzen
  document.title = title ? `${title} | Vetopedia` : defaultMetaData.title;

  // Standard Meta Tags setzen
  setMetaTag('name', 'description', description || defaultMetaData.description);
  setMetaTag('name', 'keywords', (keywords || defaultMetaData.keywords).join(','));
  setMetaTag('name', 'author', author || defaultMetaData.author);

  // Open Graph Tags setzen
  setMetaTag('property', 'og:url', url || document.location.href);
  setMetaTag('property', 'og:type', type || defaultMetaData.type);
  setMetaTag('property', 'og:title', title || defaultMetaData.title);
  setMetaTag('property', 'og:description', description || defaultMetaData.description);
  setMetaTag('property', 'og:image', image || defaultMetaData.image);
  setMetaTag('property', 'og:locale', locale || defaultMetaData.locale);
}

export function setDefaultMetaData() {
  setMetaData({});
}

const setMetaTag = (attr: string, name: string, content: string) => {
  let element = document.head.querySelector(`[${attr}="${name}"]`);
  if (!element) {
    element = document.createElement('meta');
    element.setAttribute(attr, name);
    document.head.appendChild(element);
  }
  element.setAttribute('content', content);
};
